import { HeroAccordion } from '@components/HeroAccordion'
import { HeroIcon } from '@components/HeroIcon'
import { ICourseModulesNodes } from '@dtos/Course'
import { useCourse } from '@hooks/useCourse'
import { useCourseContents } from '@hooks/useCourseContents'
import { useEnrollment } from '@hooks/useEnrollment'
import { useScrollBar } from '@hooks/useScrollbar'
import { useContentsModalStore } from '@stores/useContentsModalStore'
import {
  isContentAvailable,
  releaseContentDateText,
} from '@utils/contentAvailable'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { InView } from 'react-native-intersection-observer'
import { Text, View, XStack, YStack } from 'tamagui'
import { LessonHorizontalCard } from './LessonHorizontalCard'
import { ContentsModules } from './ContentsModules'
import { useLocalSearchParams } from 'expo-router'

interface IAccordionModuleProps {
  module: ICourseModulesNodes
  isLastModule?: boolean
  isSubmodule?: boolean
  isModuleOpen?: boolean
}

export function AccordionModule({
  module,
  isLastModule,
  isSubmodule,
  isModuleOpen,
}: IAccordionModuleProps) {
  const [moduleState, setModuleState] = useState<ICourseModulesNodes>(module)
  const [canLoadMoreContents, setCanLoadMoreContents] = useState(true)
  const { isOpen: isContentsModalOpen } = useContentsModalStore()
  const { getCourseContents } = useCourseContents()
  const { moduleId } = useLocalSearchParams()
  const { scrollTo } = useScrollBar()
  const { course } = useCourse()
  const { enrollment } = useEnrollment()
  const modulePositionRef = useRef(0)
  const scrollTimeoutRef = useRef<NodeJS.Timeout>()
  const lastModuleBorderBottomWidth = isLastModule
    ? '$border-width-hairline'
    : '$border-width-none'

  const isOpenAccordionDefault = isModuleOpen ? module.id.toString() : ''

  const handleScroll = useCallback(
    (y: number) => {
      if (y <= 0) return

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }

      scrollTimeoutRef.current = setTimeout(() => {
        scrollTo('contents-modal-scroll', y)
      }, 300)
    },
    [scrollTo],
  )

  useEffect(() => {
    if (
      isContentsModalOpen &&
      isOpenAccordionDefault &&
      modulePositionRef.current > 0
    ) {
      handleScroll(modulePositionRef.current)
    }
  }, [isContentsModalOpen, isOpenAccordionDefault, handleScroll])

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  const loadMoreContents = useCallback(async () => {
    if (!canLoadMoreContents) return
    const dataCourseContents = await getCourseContents?.(
      `${moduleState.id}`,
      moduleState.courseContents.nodes.length,
    )

    const newModuleLessons =
      dataCourseContents?.course.courseModules?.nodes[0]?.courseContents?.nodes

    if (newModuleLessons && newModuleLessons.length > 0) {
      setCanLoadMoreContents(true)
      setModuleState((prevState) => ({
        ...prevState,
        courseContents: {
          nodes: [...prevState.courseContents.nodes, ...newModuleLessons],
        },
      }))
    } else {
      setCanLoadMoreContents(false)
    }
  }, [
    canLoadMoreContents,
    getCourseContents,
    moduleState.id,
    moduleState.courseContents.nodes.length,
  ])

  const isLessonCompleted = useCallback(
    (lessonId: number) => {
      return enrollment?.progressCard?.lessonsProgresses?.find(
        (lesson) => lesson.lessonId === lessonId,
      )
    },
    [enrollment],
  )

  return (
    <View
      borderTopWidth={1}
      borderColor="$neutral-200"
      borderBottomWidth={lastModuleBorderBottomWidth}
      onLayout={(event) => {
        const y = event.nativeEvent.layout.y
        if (y > 0) {
          modulePositionRef.current = y
        }
      }}
    >
      <HeroAccordion
        key={module.id}
        value={module.id.toString()}
        defaultValue={isOpenAccordionDefault}
        marginVertical="$spacing-inset-xs"
        accordionTitle={
          <YStack
            gap="$spacing-inset-nano"
            alignItems="flex-start"
            paddingHorizontal="$spacing-inset-sm"
            width="90%"
          >
            {isSubmodule && (
              <XStack
                borderBottomLeftRadius="$border-radius-lg"
                borderTopRightRadius="$border-radius-lg"
                bg="$neutral-100"
                alignItems="center"
                paddingHorizontal="$spacing-inset-nano"
                paddingVertical="$spacing-inset-quarck"
                gap={10}
                alignSelf="flex-start"
              >
                <HeroIcon
                  icon={['fal', 'turn-down-right']}
                  color="$neutral-600"
                  size={16}
                />
                <Text
                  color="$neutral-600"
                  ff="$mulishSemiBold"
                  fontWeight="600"
                  fontSize="$font-size-xxxs"
                >
                  Submódulo
                </Text>
              </XStack>
            )}
            <Text
              fontSize="$font-size-xs"
              fontFamily="$mulishSemiBold"
              fontWeight="600"
              color="$neutral-800"
              paddingRight="$spacing-inset-xs"
            >
              {moduleState.name}
            </Text>
            <Text
              fontSize="$font-size-xxs"
              fontFamily="$mulishRegular"
              color="$neutral-500"
              fontWeight="400"
            >
              {moduleState.courseContentsCount} conteúdos
            </Text>
          </YStack>
        }
      >
        {moduleState.courseContents.nodes.map((content, index) => {
          const dataAvailable = {
            releaseAt: content.lesson.releaseAt,
            releaseAfter: content.lesson.releaseAfter,
            lockAt: content.lesson.lockAt,
            lockAfter: content.lesson.lockAfter,
            createdAt: enrollment?.activatedAt || '',
          }
          const isAntePenultimateContent =
            moduleState.courseContents.nodes.length - 3 === index

          const isCourseLinear = course?.linearRequirements
          const previousContent =
            moduleState.courseContents.nodes[index - 1 || 0]
          const isPreviousContentCompleted =
            index === 0 ? true : isLessonCompleted(previousContent.lesson.id)
          const checkIfContentIsAvailable =
            isContentAvailable(dataAvailable) &&
            (isPreviousContentCompleted || !isCourseLinear)

          const lessonCardProps = {
            dataLesson: content.lesson,
            isAvailable: Boolean(checkIfContentIsAvailable),
            availableLabel: releaseContentDateText(dataAvailable),
            hasNoContent: !content.lesson.media && !content.lesson.activity,
            courseContentsId: content.id,
            moduleId: moduleState.id,
            media: {
              ...content.lesson.media,
              coverImage: content.lesson.coverImage,
            },
            isCompleted: isLessonCompleted(content.lesson.id)?.completed,
          }

          return (
            <React.Fragment key={content.id}>
              {isAntePenultimateContent ? (
                <InView
                  onChange={(inView) =>
                    inView && canLoadMoreContents && loadMoreContents()
                  }
                  threshold={0.5}
                >
                  <LessonHorizontalCard key={content.id} {...lessonCardProps} />
                </InView>
              ) : (
                <LessonHorizontalCard key={content.id} {...lessonCardProps} />
              )}
            </React.Fragment>
          )
        })}
        {module.courseModules?.map((submodule) => {
          const isSubmoduleOpen = submodule.id.toString() === moduleId
          return (
            <ContentsModules
              key={submodule.id}
              module={submodule}
              moduleId={submodule.id.toString()}
              isLastModule={false}
              isModuleOpen={isSubmoduleOpen}
              isSubmodule={true}
            />
          )
        })}
      </HeroAccordion>
    </View>
  )
}
